import type BaseFolderId from 'owa-service/lib/contract/BaseFolderId';
import constant from 'owa-service/lib/factory/constant';
import fieldURIOrConstantType from 'owa-service/lib/factory/fieldURIOrConstantType';
import type FindConversationResponseMessage from 'owa-service/lib/contract/FindConversationResponseMessage';
import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import isEqualTo from 'owa-service/lib/factory/isEqualTo';
import propertyUri from 'owa-service/lib/factory/propertyUri';
import restrictionType from 'owa-service/lib/factory/restrictionType';
import seekToConditionPageView from 'owa-service/lib/factory/seekToConditionPageView';
import type SortResults from 'owa-service/lib/contract/SortResults';
import type ViewFilter from 'owa-service/lib/contract/ViewFilter';
import { findConversation } from './findConversation';
import { logLoadedConversationIds } from './utils/logFindMailResponse';

/**
 * The FindConversationWithSeek to condition is based on the instance key of the last row present locally in the table.
 * This is currently used for load more scenario
 * @param folderId - The folderId for which we are fetching the data
 * @param instanceKey - We use instancekey of the last item we have in the loaded rows in the table.
 * @param rowsToLoad - the number of rows to load
 * @param viewFilter - view filter (e.g - Unread\Flag)
 * @param sortResults - sort by properties
 * @param findConversationShapeName - the shape for the request
 * @param options - optional parameters to be passed to request (method and/or headers)
 */
export function findConversationWithSeekToCondition(
    folderId: BaseFolderId,
    instanceKey: string,
    rowsToLoad: number,
    viewFilter: ViewFilter,
    sortResults: SortResults[],
    focusedViewFilter: FocusedViewFilter,
    findConversationShapeName: string
): Promise<FindConversationResponseMessage> {
    const restriction = isEqualTo({
        FieldURIOrConstant: fieldURIOrConstantType({
            Item: constant({
                Value: instanceKey,
            }),
        }),
        Item: propertyUri({
            FieldURI: 'ConversationInstanceKey',
        }),
    });

    const seekToCondition = restrictionType({
        Item: restriction,
    });

    const paging = seekToConditionPageView({
        BasePoint: 'Beginning',
        Condition: seekToCondition,
        MaxEntriesReturned: rowsToLoad,
    });

    return findConversation({
        folderId,
        paging,
        viewFilter,
        sortResults,
        focusedViewFilter,
        findConversationShapeName,
    }).then(logLoadedConversationIds);
}
